import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { alpha } from '@mui/material/styles';
import { type ComponentType, useState } from 'react';

import { Input } from 'payments/components/Input';
import { PaymentTextInputAdornment } from 'payments/components/PaymentTextInputAdornment';
import { SOY } from 'payments/constants/colors';
import { FONT_NAMES } from 'payments/constants/fonts';
import { type CardFieldState } from 'payments/types/card';
import { type PaymentProviderType } from 'payments/types/providers';
import useContrastTextColor from 'reservations/brand/hooks/useContrastTextColor';

export type CardFieldsContainerProps = {
  name: PaymentProviderType;
  label: string;
  inputEl: ComponentType;
  fieldState?: CardFieldState;
};

export type InputLabelPropsSxProps = {
  marginTop: string;
  color?: string;
};

export interface CardFieldProps extends CardFieldsContainerProps {
  error: string | null;
  isEmpty: boolean;
  isFocused: boolean;
  setError: Function;
  setIsEmpty: Function;
  setIsFocused: Function;
  InputLabelPropsSx: InputLabelPropsSxProps;
  InputPropsSx?: object;
  borderColor?: string;
}

export const useCardFieldProps = () => {
  const [error, setError] = useState<string | null>(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [isFocused, setIsFocused] = useState(false);

  return {
    error,
    isEmpty,
    isFocused,
    setError,
    setIsEmpty,
    setIsFocused
  };
};

export const CloverCardField = ({
  fieldState,
  ...props
}: CardFieldsContainerProps) => {
  const { error, isEmpty, isFocused, ...cardFieldProps } = useCardFieldProps();
  const contrastColor = useContrastTextColor();

  return (
    <CardField
      error={error || (fieldState?.error ?? null)}
      isEmpty={
        isEmpty && (fieldState?.isEmpty ?? /* istanbul ignore next */ true)
      }
      isFocused={isFocused || (fieldState?.isFocused ?? false)}
      InputPropsSx={{}}
      InputLabelPropsSx={{ marginTop: '5px' }}
      borderColor={contrastColor}
      {...props}
      {...cardFieldProps}
    />
  );
};

export const StripeCardField = (props: CardFieldsContainerProps) => {
  const cardFieldProps = useCardFieldProps();
  const InputLabelPropsSx = {
    marginTop: '5px',
    '&.Mui-focused': {
      marginTop: 0
    }
  };
  return (
    <CardField
      InputLabelPropsSx={InputLabelPropsSx}
      {...props}
      {...cardFieldProps}
    />
  );
};

export const CardField = ({
  error,
  inputEl,
  isEmpty,
  isFocused,
  label,
  name,
  setError,
  setIsEmpty,
  setIsFocused,
  InputLabelPropsSx,
  InputPropsSx = {},
  borderColor
}: CardFieldProps) => (
  <Box>
    <TextField
      name={name}
      label={label}
      variant="outlined"
      size="small"
      error={!!error}
      helperText={error}
      required
      fullWidth
      InputLabelProps={{
        htmlFor: name,
        shrink: isFocused || !isEmpty,
        sx: { ...InputLabelPropsSx }
      }}
      InputProps={{
        endAdornment: (
          <PaymentTextInputAdornment name={name} hasError={!!error} />
        ),
        inputComponent: Input,
        inputProps: {
          component: inputEl,
          label: '',
          options: {
            placeholder: '',
            style: {
              base: {
                fontSize: '16px',
                fontFamily: FONT_NAMES.reg,
                color: SOY,
                padding: '14px 12px'
              },
              invalid: {
                color: 'inherit'
              }
            }
          },
          onFocus: /* istanbul ignore next */ () => setIsFocused(true),
          onBlur: /* istanbul ignore next */ () => setIsFocused(false),
          onChange: /* istanbul ignore next */ (e: any) => {
            if (e.empty !== isEmpty) {
              setIsEmpty(e.empty);
            }

            const errorMessage = e.error?.message ?? null;
            if (errorMessage !== error) {
              setError(errorMessage);
            }
          }
        },
        sx: { ...InputPropsSx }
      }}
      sx={
        borderColor
          ? {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(borderColor, 0.8)
              },
              '&:hover': {
                borderColor: alpha(borderColor, 0.8)
              }
            }
          : {}
      }
    />
  </Box>
);
